// SelectedOrganization.jsx – Pure White Text Version
import React, { Fragment } from "react";
import {
  Box,
  Text,
  Divider,
  Image,
  useDisclosure,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Button,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { IoIosArrowDown } from "react-icons/io";
import { HiOutlineOfficeBuilding, HiOutlineUser } from "react-icons/hi";
import { PiPlugsConnectedFill } from "react-icons/pi";
import { CiSettings } from "react-icons/ci";
import { FaUserTie } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../Services/AuthService";
import { getTokenFromLoacalStorage } from "../_helpers/helpers";
import OrganizationSelectionModal from "./OrganizationSelectionModal";

function SelectedOrganization() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();

  const tokenData = getTokenFromLoacalStorage();
  const selectedOrg = tokenData?.selectedOrg ? JSON.parse(tokenData.selectedOrg) : {};
  const isAllBanks = tokenData?.user_data?.extraData?.isAllBanks || false;
  const isSuperAdmin = tokenData?.isSuperAdmin || false;

  return (
    <Fragment>
      <Popover placement="top-start">
        <PopoverTrigger>
          <Box
            cursor="pointer"
            p={4}
            borderRadius="lg"
            bg="rgba(255, 255, 255, 0.06)"
            backdropFilter="blur(12px)"
            border="1px solid rgba(167, 139, 255, 0.25)"
            transition="all 0.3s ease"
            _hover={{
              bg: "rgba(167, 139, 255, 0.18)",
              borderColor: "rgba(167, 139, 255, 0.5)",
              transform: "translateY(-2px)",
            }}
          >
            <Flex align="center" gap={3}>
              {/* Org Icon */}
              {!isAllBanks ? (
                <Image
                  src={selectedOrg.orgIcon || selectedOrg.iconUrl || "/images/defaultorgicon.png"}
                  w="44px"
                  h="44px"
                  borderRadius="full"
                  objectFit="cover"
                  border="2px solid rgba(167, 139, 255, 0.4)"
                  fallbackSrc="/images/defaultorgicon.png"
                />
              ) : (
                <Box
                  w="44px"
                  h="44px"
                  borderRadius="full"
                  bg="rgba(167, 139, 255, 0.2)"
                  border="2px solid rgba(167, 139, 255, 0.5)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={HiOutlineOfficeBuilding} boxSize={6} color="white" />
                </Box>
              )}

              {/* Org Name & Domain */}
              {!isAllBanks && (
                <Box flex="1" minW="0">
                  <Text fontSize="sm" fontWeight="600" color="white" noOfLines={1}>
                    {selectedOrg?.name || "Select Organization"}
                  </Text>
                  <Text fontSize="xs" color="white" opacity={0.8} noOfLines={1}>
                    {selectedOrg?.domain || ""}
                  </Text>
                </Box>
              )}

              <Icon as={IoIosArrowDown} color="white" fontSize="20" />
            </Flex>
          </Box>
        </PopoverTrigger>

        <Portal>
          <PopoverContent
            bg="rgba(15, 15, 35, 0.98)"
            backdropFilter="blur(16px)"
            border="1px solid rgba(167, 139, 255, 0.35)"
            boxShadow="0 10px 40px rgba(0, 0, 0, 0.7)"
            color="white"
            maxW="340px"
            _focus={{ outline: "none" }}
          >
            {/* Current Organization Header */}
            {!isAllBanks && (
              <PopoverHeader border="0" pb={3} pt={4}>
                <Flex align="center" gap={3}>
                  <Image
                    src={selectedOrg.orgIcon || "/images/defaultorgicon.png"}
                    w="42px"
                    h="42px"
                    borderRadius="full"
                    border="2px solid #a78bff"
                    fallbackSrc="/images/defaultorgicon.png"
                  />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="white">
                      {selectedOrg?.name}
                    </Text>
                    <Text fontSize="xs" color="white" opacity={0.75}>
                      {selectedOrg?.domain}
                    </Text>
                  </Box>
                </Flex>
              </PopoverHeader>
            )}

            <PopoverBody p={0}>
              <Divider borderColor="rgba(167, 139, 255, 0.3)" />

              {/* Menu Items */}
              <Box>
                {!isAllBanks && (
                  <Box p={4} pb={2}>
                    <Flex align="center" mb={3} color="white">
                      <Icon as={HiOutlineOfficeBuilding} mr={3} />
                      <Text color={'#fff'} fontSize="sm" fontWeight="medium">
                        Organization
                      </Text>
                    </Flex>
                    <Flex gap={6} fontSize="sm">
                      <Text
                        as={Link}
                        to="/organization-add"
                        color="white"
                        opacity={0.9}
                        _hover={{ color: "#c4b5fd", textDecoration: "underline" }}
                      >
                        Add+
                      </Text>
                      <Text
                        cursor="pointer"
                        onClick={onOpen}
                        color="white"
                        opacity={0.9}
                        _hover={{ color: "#c4b5fd", textDecoration: "underline" }}
                      >
                        Switch
                      </Text>
                    </Flex>
                  </Box>
                )}

                <Divider borderColor="rgba(167, 139, 255, 0.2)" />

                {/* Navigation Links */}
                <Box  >
                  <MenuItemRow color={'#fff'} icon={HiOutlineUser} to="/profile">
                    Update Profile
                  </MenuItemRow>
                  <MenuItemRow color={'#fff'} icon={PiPlugsConnectedFill} to="/integrations/platforms">
                    Integrations
                  </MenuItemRow>
                  <MenuItemRow color={'#fff'} icon={CiSettings} to="/settings/orginfo">
                    Settings
                  </MenuItemRow>

                  {isSuperAdmin && (
                    <MenuItemRow color={'#fff'} icon={FaUserTie} to="/su/manager">
                      Super Administrator
                    </MenuItemRow>
                  )}
                </Box>
              </Box>
            </PopoverBody>

            <PopoverFooter border="0" pt={3} pb={4}>
              <Button
                variant="link"
                color="#ff6b6b"
                fontWeight="medium"
                size="sm"
                onClick={() => logout(dispatch)}
                _hover={{ color: "#ff8787" }}
              >
                Log Out
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </Portal>
      </Popover>

      <OrganizationSelectionModal isOpen={isOpen} onClose={onClose} />
    </Fragment>
  );
}

// Reusable white-text menu row
const MenuItemRow = ({ icon: IconComp, children, to }) => (
  <Box
    as={Link}
    to={to}
    px={4}
    py="14px"
    display="flex"
    alignItems="center"
    color="#fff"
    fontSize="sm"
    transition="all 0.2s"
    _hover={{
      bg: "rgba(167, 139, 255, 0.18)",
      pl: "20px",
    }}
  >
    <Icon as={IconComp} mr={3} color="white" opacity={0.9} />
    <Text color={'#fff'} >{children}</Text>
  </Box>
);

export default SelectedOrganization;